import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Calendar, CheckSquare, Users, History, LogOut, Sparkles, Package, ClipboardList, Building2, FileText, Contact2, Home, StickyNote, MapPin, Bug, Menu, X } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";

const LOGO = "/logo-aae.png";
const roleLabel = { admin: "Administrateur", manager: "Responsable", employee: "Employé" };

const NavItem = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
        isActive
          ? "bg-primary text-primary-foreground shadow-warm"
          : "text-foreground/70 hover:bg-muted hover:text-foreground"
      }`
    }
  >
    <Icon className="h-4 w-4" strokeWidth={2} />
    {label}
  </NavLink>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canValidate = user?.role === "manager" || user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const [mobileOpen, setMobileOpen] = useState(false);
  // Fermeture auto du drawer au changement de route
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Bloquer le scroll body quand le drawer est ouvert
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const navContent = (
    <>
      <NavItem to="/" icon={Home} label="Accueil" testid="nav-home" />
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-4 pt-2 pb-1">Activité</div>
      <NavItem to="/evenements-kanban" icon={Sparkles} label="Événements" testid="nav-events" />
      <NavItem to="/materiel" icon={Package} label="Matériel" testid="nav-equipment" />
      <NavItem to="/declarations" icon={FileText} label="Déclarations" testid="nav-declarations" />
      <NavItem to="/intermittents" icon={ClipboardList} label="Intermittents" testid="nav-intermittents" />
      <NavItem to="/clients" icon={Building2} label="Clients" testid="nav-clients" />
      <NavItem to="/contacts" icon={Contact2} label="Prestataires / Artistes" testid="nav-contacts" />
      <NavItem to="/lieux" icon={MapPin} label="Lieux" testid="nav-places" />
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-4 pt-3 pb-1">RH</div>
      <NavItem to="/semaine" icon={Calendar} label="Ma semaine" testid="nav-week" />
      <NavItem to="/historique" icon={History} label="Historique" testid="nav-history" />
      <NavItem to="/notes-service" icon={StickyNote} label="Notes de service" testid="nav-service-notes" />
      {canValidate && (
        <NavItem to="/validation" icon={CheckSquare} label="Validation" testid="nav-validation" />
      )}
      {isAdmin && (
        <>
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-4 pt-3 pb-1">Admin</div>
          <NavItem to="/equipe" icon={Users} label="Équipe" testid="nav-team" />
          <NavItem to="/erreurs" icon={Bug} label="Journal erreurs" testid="nav-errors" />
        </>
      )}
    </>
  );

  const userFooter = (
    <div className="rounded-lg border border-border/60 p-3 bg-muted space-y-2">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Connecté</div>
        <div className="font-medium text-sm truncate" data-testid="current-user-name">{user?.name}</div>
        <div className="text-xs text-muted-foreground">{roleLabel[user?.role] || user?.role}</div>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="w-full justify-center rounded-full border-destructive/40 text-destructive hover:bg-destructive hover:text-white font-semibold"
        onClick={async () => { await logout(); navigate("/login"); }}
        data-testid="logout-btn"
      >
        <LogOut className="h-4 w-4 mr-2" /> Se déconnecter
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      <aside className="w-64 shrink-0 border-r border-border/60 bg-card px-4 py-6 hidden md:flex md:flex-col gap-6 sticky top-0 h-screen overflow-hidden">
        <div className="px-2 flex flex-col items-center">
          <img src={LOGO} alt="Antoine Événements" className="h-16 w-auto" />
          <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-primary text-center">COMPAGNON</div>
        </div>

        <nav className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto -mr-2 pr-2">
          {navContent}
        </nav>

        <div className="shrink-0">
          {userFooter}
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-card backdrop-blur-md border-b border-border/60 px-4 py-3 flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-open"
          aria-label="Ouvrir le menu"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <img src={LOGO} alt="AAE" className="h-8 w-auto" />
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={async () => { await logout(); navigate("/login"); }} data-testid="mobile-logout-btn" aria-label="Se déconnecter">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-menu-overlay"
          />
          <aside
            className="md:hidden fixed top-0 left-0 bottom-0 z-50 w-72 max-w-[85vw] bg-card border-r border-border/60 flex flex-col gap-4 px-4 py-5 shadow-2xl animate-in slide-in-from-left"
            data-testid="mobile-menu-drawer"
          >
            <div className="flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <img src={LOGO} alt="AAE" className="h-10 w-auto" />
                <div className="text-[10px] font-bold uppercase tracking-widest text-primary">COMPAGNON</div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <nav className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto -mr-2 pr-2">
              {navContent}
            </nav>
            <div className="shrink-0">
              {userFooter}
            </div>
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 pt-20 md:pt-14 px-4 md:px-8 pb-6 md:pb-10 max-w-[1400px] mx-auto w-full app-ambient-bg">
        <ErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
